import { z } from "zod";

/**
 * Contract for the `send_email` action node.
 *
 * `send_email` earns a contract because its publish-time validity is a
 * cross-field rule the legacy `requiredFields` list cannot express: at least
 * one recipient is required, from a static/templated `to` address and/or a
 * `replyTo` block (which derives recipients from a previously-seen email at
 * run time). The two are ADDITIVE — the reply contributes the parent's
 * sender (and, in `all` mode, its other recipients), and a `to` adds further
 * recipients, like Reply in an email client.
 *
 * `subject` is required for a fresh email, but OPTIONAL when `replyTo` is
 * set: a reply defaults its subject to `Re: <the parent's subject>` at run
 * time (RFC 5322 §3.6.5 convention; the worker also threads the reply via
 * In-Reply-To/References), so the author doesn't have to retype it. A typed
 * subject still wins. `body` is optional (an empty body, or AI-drafted body,
 * is allowed). `cc` is an optional list of additional recipients.
 *
 * The schema is otherwise permissive (`.passthrough()`) so adding a future
 * config key doesn't require a schema bump, mirroring create_task.
 */
export const sendEmailInputSchema = z.object({}).passthrough();

/**
 * How a reply derives its recipients from the parent message:
 *   - `sender` — reply to the parent's From only (classic Reply).
 *   - `all`    — also carry the parent's To/Cc forward, minus the sending
 *                identity itself (classic Reply All).
 */
export const sendEmailReplyModeSchema = z.enum(["sender", "all"]);

export type SendEmailReplyMode = z.infer<typeof sendEmailReplyModeSchema>;

/** Default reply source — the email that triggered the run. */
export const DEFAULT_REPLY_SOURCE = "{{ input.inboundEmailId }}";

export const sendEmailSenderSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("quovy") }),
  z.object({ kind: z.literal("mailbox"), mailboxId: z.string().min(1) }),
  z.object({ kind: z.literal("email"), source: z.string().trim().min(1) }),
]);

export type SendEmailSender = z.infer<typeof sendEmailSenderSchema>;

export const sendEmailReplyToSchema = z.object({
  /**
   * Template expression resolving to the public id (`emsg_*`) of the email
   * being replied to. Defaults to the triggering email
   * (`{{ input.inboundEmailId }}`) when omitted, which is what an
   * email-triggered workflow wants. Any upstream step output that yields an
   * `emsg_*` id may be referenced instead.
   *
   * Resolution happens in the async intent handler, NOT the step function —
   * the parent row has to be read from the database to derive recipients and
   * subject, and step functions are synchronous.
   */
  source: z.string().optional(),
  mode: sendEmailReplyModeSchema,
});

export const sendEmailConfigSchema = z
  .object({
    /**
     * Static / templated recipient address(es). Additive with `replyTo` —
     * both can be set.
     */
    to: z.string().optional(),
    /**
     * Reply configuration. When present, the send is threaded against the
     * resolved parent email (In-Reply-To/References) and inherits its
     * recipients per `mode`. Omit for a fresh, unthreaded email.
     */
    replyTo: sendEmailReplyToSchema.optional(),
    /** Required, explicit From selection. The runtime never guesses a sender. */
    sender: sendEmailSenderSchema,
    /** Optional Cc recipients — static or templated addresses. */
    cc: z.array(z.string()).optional(),
    subject: z.string().optional(),
    /** Verbatim body, or — when draftWithAi is on — guidance for the agent. */
    body: z.string().optional(),
    /**
     * When true, an agent drafts the body from the run context using `body`
     * as guidance (recipients, subject and attachments stay author-controlled).
     */
    draftWithAi: z.boolean().optional(),
    /** Upstream-output template expressions resolving to attachable file ids. */
    attachments: z.array(z.string()).optional(),
  })
  .passthrough()
  .superRefine((cfg, ctx) => {
    const hasStaticTo = typeof cfg.to === "string" && cfg.to.trim().length > 0;
    const isReply = cfg.replyTo != null;
    // Need at least one recipient source — a static To and/or a reply.
    if (!hasStaticTo && !isReply) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["to"],
        message: "Add a recipient — enter a To address or reply to an earlier email",
      });
    }
    // An explicitly blank reply source is an author error: omitting `source`
    // means "the triggering email", but a whitespace-only string resolves to
    // nothing and would fail at run time instead of at publish.
    if (cfg.replyTo && typeof cfg.replyTo.source === "string" && !cfg.replyTo.source.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["replyTo", "source"],
        message: "Choose which email to reply to, or leave it unset to use the triggering email",
      });
    }
    // Subject is required for a fresh email, but optional when replying — the
    // worker defaults it to `Re: <the parent's subject>` at run time.
    const hasSubject = typeof cfg.subject === "string" && cfg.subject.trim().length > 0;
    if (!hasSubject && !isReply) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["subject"],
        message: "Subject is required",
      });
    }
  });

/**
 * Name of the `workflow_events` row a parked `send_email` step waits on, and
 * that the send worker emits once the message reaches a terminal status.
 *
 * Keyed on the step's GRAPH NODE id, not the message public id, so the step can
 * compute it synchronously — before the `emsg_*` id exists. Safe because
 * `workflow_events` is unique on `(workflow_run_id, name)` and a `send_email`
 * step queues at most one message per run.
 *
 * Lives here, in the package both sides already depend on, because the emitter
 * (`emitEmailTerminalEvent`, packages/db) and the waiter (`sendEmail`,
 * apps/worker) disagreeing on this string is a silent permanent hang rather
 * than a compile error.
 */
export function emailSendEventName(stepNodeId: string): string {
  return `email.${stepNodeId}.completed`;
}

export const sendEmailOutputSchema = z.object({
  /** Public id (`emsg_*`) of the queued/sent email message. Null only if resume state was corrupt. */
  emailMessagePublicId: z.string().nullable(),
  /** Terminal status of the send (`sent` on the success path that reaches this output). */
  status: z.string(),
  /** Provider message id of the sent email; null when the mailer returned none. */
  messageId: z.string().nullable(),
});

export type SendEmailOutput = z.infer<typeof sendEmailOutputSchema>;
